import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';
import { fieldTypeOptions } from '../../../utils/common';

import {
  crmsAdd,
  crmsDetailsGetById,
  crmsUpdate
} from '../../../store/slices/tenantSlices/configurationSlice';
import FormTextarea from '../../../components/form/FormTextarea';

const AddViewCrmsModal = ({ isOpen, onCloseClick, recordId = null, isViewOnly = false }) => {
  const dispatch = useDispatch();

  const [fieldData, setFieldData] = useState([
    {
      edit: 'true',
      label: '',
      type: 'text',
      option: ''
    }
  ]);

  const [fieldsToAdd, setFieldsToAdd] = useState(1);

  const initialValues = {
    name: '',
    customerMessage: '',
    fields: []
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    customerMessage: Yup.string().required('Message is required')
  });

  const onSubmit = async () => {
    if (!isViewOnly && recordId) {
      try {
        const payload = { ...values, id: recordId };
        const fData = [];
        fieldData.forEach((item, index) =>
          fData.push({ ...item, field_rank: `custom_field_${index + 1}` })
        );
        payload['fields'] = fData;
        const response = await dispatch(crmsUpdate(payload)).unwrap();
        if (response) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          setFieldsToAdd(1);
          setFieldData([
            {
              edit: 'true',
              label: '',
              type: 'text',
              option: ''
            }
          ]);
          resetForm();
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values };
        const fData = [];
        fieldData.forEach((item, index) =>
          fData.push({ ...item, field_rank: `custom_field_${index + 1}` })
        );
        payload['fields'] = fData;
        const response = await dispatch(crmsAdd(payload)).unwrap();
        if (response) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          setFieldsToAdd(1);
          setFieldData([
            {
              edit: 'true',
              label: '',
              type: 'text',
              option: ''
            }
          ]);
          resetForm();
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const { values, handleChange, handleBlur, handleSubmit, touched, errors, resetForm, setValues } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const handleFieldChange = (key, value, index) => {
    const data = [...fieldData];
    data[index][key] = value;
    setFieldData(data);
  };

  const fetchCrmRecord = async () => {
    try {
      const response = await dispatch(crmsDetailsGetById(recordId)).unwrap();
      setValues({ ...response });
      setFieldData(
        response.fields || [
          {
            edit: 'true',
            label: '',
            type: 'text',
            option: ''
          }
        ]
      );
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (recordId) {
      fetchCrmRecord();
    }
  }, [recordId]);

  const handleAddFields = () => {
    const newFields = Array.from({ length: fieldsToAdd }, () => ({
      edit: 'true',
      label: '',
      type: 'text',
      option: ''
    }));
    setFieldData([...fieldData, ...newFields]);
  };

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">
          {isViewOnly ? 'CRMS Details' : recordId ? 'Edit CRMS' : 'Add CRMS'}
        </div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                disabled={isViewOnly}
              />
            </Col>
            <Col lg="5">
              <FormTextarea
                name="customerMessage"
                label="Description"
                placeholder="Enter description"
                value={values.customerMessage}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                rows={1}
                disabled={isViewOnly}
              />
            </Col>
          </Row>
          {fieldData?.map((val, index, array) => {
            return (
              <Row key={index}>
                <Col lg="2">
                  <FormToggle
                    name="edit"
                    label="Edit"
                    value={val.edit}
                    onChange={(e) => handleFieldChange('edit', e, index)}
                    disabled={isViewOnly}
                  />
                </Col>
                <Col lg="3">
                  <FormInput
                    name="label"
                    label={`Label ( custom_field_${index + 1} )`}
                    placeholder="Enter Label"
                    value={val.label}
                    onChange={(e) => handleFieldChange('label', e.target.value, index)}
                    disabled={isViewOnly}
                  />
                </Col>
                <Col lg="2">
                  <FormSelect
                    name="type"
                    label="Type"
                    placeholder="Select Type"
                    value={val.type}
                    options={fieldTypeOptions}
                    onChange={(e) => handleFieldChange('type', e.target.value, index)}
                    disabled={isViewOnly}
                  />
                </Col>
                {val.type === 'select' && (
                  <Col lg="3">
                    <FormInput
                      name="option"
                      label="Options"
                      placeholder="Enter Options"
                      value={val.option}
                      onChange={(e) => handleFieldChange('option', e.target.value, index)}
                      disabled={isViewOnly}
                    />
                  </Col>
                )}
                {!isViewOnly && (
                  <Col lg="2" className="d-flex flex-row align-items-center">
                    {array?.length > 1 ? (
                      <div
                        className="me-3 text-danger pt-2 cursor-pointer"
                        onClick={() => {
                          setFieldData(array?.slice(0, index).concat(array?.slice(index + 1)));
                        }}>
                        <Icon.Trash size={20} />
                      </div>
                    ) : null}
                    {array?.length - 1 === index ? (
                      <>
                        <div className="pt-2">
                          <FormSelect
                            value={fieldsToAdd}
                            options={[1, 5, 10, 15, 20].map((item) => ({
                              label: item,
                              value: item
                            }))}
                            onChange={(e) => setFieldsToAdd(e.target.value)}
                            style={{ width: '80px' }}
                          />
                        </div>
                        <div
                          className="text-info ms-3 pt-2 cursor-pointer"
                          onClick={handleAddFields}>
                          <Icon.Plus size={20} />
                        </div>
                      </>
                    ) : null}
                  </Col>
                )}
              </Row>
            );
          })}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            setFieldsToAdd(1);
            setFieldData([
              {
                edit: 'true',
                label: '',
                type: 'text',
                option: ''
              }
            ]);
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        {!isViewOnly && (
          <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
            {recordId ? 'Update' : 'Save'}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

AddViewCrmsModal.propTypes = {
  isOpen: PropTypes.bool,
  isViewOnly: PropTypes.bool,
  onCloseClick: PropTypes.func,
  recordId: PropTypes.any
};

export default AddViewCrmsModal;
